/**
 * 初始化代码高亮和交互功能
 * 在DOM加载完成后为代码块添加交互功能
 */

export function initializeCodeHighlighter() {
  const setupObserver = () => {
    const pendingBlocks = new Set();
    let rafId = null;

    const flushPendingBlocks = () => {
      rafId = null;
      pendingBlocks.forEach((block) => {
        if (document.body.contains(block)) {
          enhanceCodeBlock(block);
        }
      });
      pendingBlocks.clear();
    };

    const scheduleEnhance = (block) => {
      pendingBlocks.add(block);
      if (!rafId) {
        rafId = window.requestAnimationFrame(flushPendingBlocks);
      }
    };

    // 监视DOM变化，仅在包含代码块的新节点插入时批量处理，减轻流式渲染与页面跳转时的主线程压力
    const observer = new MutationObserver((mutations) => {
      for (let i = 0; i < mutations.length; i++) {
        const addedNodes = mutations[i].addedNodes;
        for (let j = 0; j < addedNodes.length; j++) {
          const node = addedNodes[j];
          if (node.nodeType !== Node.ELEMENT_NODE) continue;

          if (node.classList?.contains('code-with-line-numbers')) {
            scheduleEnhance(node);
          } else if (node.childElementCount > 0) {
            const codeBlocks = node.getElementsByClassName('code-with-line-numbers');
            for (let k = 0; k < codeBlocks.length; k++) {
              scheduleEnhance(codeBlocks[k]);
            }
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    document.querySelectorAll('.code-with-line-numbers').forEach(enhanceCodeBlock);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupObserver, { once: true });
  } else {
    setupObserver();
  }
}

/**
 * 为代码块添加交互功能
 * @param {HTMLElement} codeBlock - 代码块元素
 */
function enhanceCodeBlock(codeBlock) {
  // 添加复制按钮
  addCopyButton(codeBlock);
  
  // 添加行点击高亮功能
  addLineHighlighting(codeBlock);
}

/**
 * 为代码块添加复制按钮
 * @param {HTMLElement} codeBlock - 代码块元素
 */
function addCopyButton(codeBlock) {
  // 检查是否已经有复制按钮
  if (codeBlock.querySelector('.copy-code-btn')) {
    return;
  }
  
  const copyBtn = document.createElement('button');
  copyBtn.className = 'copy-code-btn';
  copyBtn.innerHTML = '<i class="ri-clipboard-line"></i>';
  copyBtn.title = '复制代码';
  
  // 添加复制功能
  copyBtn.addEventListener('click', () => {
    // 获取代码块中的纯文本
    const codeLines = Array.from(codeBlock.querySelectorAll('.code-line'))
      .map(line => line.textContent)
      .join('\n');
    
    // 复制到剪贴板
    navigator.clipboard.writeText(codeLines)
      .then(() => {
        // 显示复制成功状态
        copyBtn.innerHTML = '<i class="ri-check-line"></i>';
        copyBtn.style.color = 'var(--success-color)';
        
        // 2秒后恢复原始图标
        setTimeout(() => {
          copyBtn.innerHTML = '<i class="ri-clipboard-line"></i>';
          copyBtn.style.color = '';
        }, 2000);
      })
      .catch(err => {
        console.error('复制失败:', err);
        copyBtn.innerHTML = '<i class="ri-error-warning-line"></i>';
        copyBtn.style.color = 'var(--error-color)';
        
        // 2秒后恢复原始图标
        setTimeout(() => {
          copyBtn.innerHTML = '<i class="ri-clipboard-line"></i>';
          copyBtn.style.color = '';
        }, 2000);
      });
  });
  
  // 将按钮添加到代码块的父元素
  const container = codeBlock.closest('.code-container') || codeBlock.parentElement;
  if (container) {
    container.style.position = 'relative';
    container.appendChild(copyBtn);
  }
}

/**
 * 为代码行添加点击高亮功能
 * @param {HTMLElement} codeBlock - 代码块元素
 */
function addLineHighlighting(codeBlock) {
  const codeLines = codeBlock.querySelectorAll('.code-line');
  
  codeLines.forEach(line => {
    // 确保行元素只绑定一次事件
    if (!line.dataset.highlightInitialized) {
      line.dataset.highlightInitialized = 'true';
      
      line.addEventListener('click', () => {
        // 移除所有行的高亮
        codeLines.forEach(l => l.classList.remove('highlight'));
        
        // 添加当前行的高亮
        line.classList.add('highlight');
      });
    }
  });
}

// 导出初始化函数
export default initializeCodeHighlighter; 